import React from 'react';

import { Box, Display, Flex, Heading, Image, Text } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box';
import { Props as DisplayProps } from '@rover/kibble/core/Display';
import { Props as FlexProps } from '@rover/kibble/core/Flex';
import type { Props as HeadingProps } from '@rover/kibble/core/Heading';
import Section, { Props as SectionProps } from '@rover/kibble/patterns/Section';
import { PetType } from '@rover/types/src/Pet';

import catProfilePhoto from './images/catProfilePhoto.png';
import catWithPaw from './images/catWithPaw.png';
import catWithPawInverted from './images/catWithPawInverted.png';
import dogWithPaw from './images/dogWithPaw.png';
import happyDog from './images/happyDog.png';
import sniffingCat from './images/sniffingCat.png';
import sniffingDog from './images/sniffingDog.png';

export type ListItemProps = Omit<BoxProps, 'tx' | 'variant'> & {
  image?: string;
  ariaLabel?: string;
};

const DOG_AND_CAT_IMAGES = [sniffingDog, dogWithPaw, catWithPaw];

const PET_TYPE_IMAGES: Partial<Record<PetType, string[]>> = {
  [PetType.CAT]: [sniffingCat, catWithPawInverted, catProfilePhoto],
  [PetType.DOG]: [sniffingDog, dogWithPaw, happyDog],
  [PetType.PUPPY]: [sniffingDog, dogWithPaw, happyDog],
};

type ListProps = FlexProps & { petType?: PetType };

const HowBrandWorks = (props: SectionProps): JSX.Element => {
  return (
    <Section py="12x" background="accent" data-qa-id="service-landing-how-brand-works" {...props} />
  );
};

type HeaderVariant = 'display' | 'heading';

type HowBrandWorksHeaderProps = (DisplayProps | HeadingProps) & {
  /** Controls the typographic style of the header. Defaults to 'display'. */
  variant?: HeaderVariant;
};

HowBrandWorks.Display = ({
  variant = 'display',
  ...headerProps
}: HowBrandWorksHeaderProps): JSX.Element => {
  if (variant === 'heading') {
    return (
      <Heading
        size="400"
        webProps={{ as: 'h3', responsive: false }}
        textAlign="center"
        pt="8x"
        pb={['0x', '8x']}
        {...(headerProps as HeadingProps)}
      />
    );
  }

  return (
    <Display
      size="500"
      webProps={{ as: 'h3', responsive: false }}
      textAlign="center"
      pt="8x"
      pb={['0x', '8x']}
      {...headerProps}
    />
  );
};

HowBrandWorks.List = (props: ListProps): JSX.Element => {
  const { petType, ...flexProps } = props;
  const images = (petType && PET_TYPE_IMAGES[petType]) || DOG_AND_CAT_IMAGES;
  const children = images
    ? React.Children.map(props.children, (child, index) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<ListItemProps>, { image: images[index] })
          : child
      )
    : props.children;
  return (
    <Flex justifyContent="center">
      <Flex webAs="ol" pl="0x" flexDirection={['column', 'row']} {...flexProps}>
        {children}
      </Flex>
    </Flex>
  );
};

HowBrandWorks.ListItem = (props: ListItemProps): JSX.Element => {
  return (
    <Box webAs="li" sx={{ listStyleType: 'none' }} flex={['1 1 auto', '1 1 33.3333%']} {...props}>
      <Flex alignItems="flex-start" px={['5x', '6x']} pb={['8x', '0x']} flexDirection="column">
        <Flex width="100%" height="170px" justifyContent="center" {...props}>
          {props.image ? (
            <Image
              src={props.image}
              aria-label={props.ariaLabel}
              width="auto"
              height="100%"
              description=""
            />
          ) : null}
        </Flex>
        <Box mt={['0x', '0x', '4x']} textAlign="center" width="100%">
          {props.children}
        </Box>
      </Flex>
    </Box>
  );
};

HowBrandWorks.ItemHeading = (props: HeadingProps & { step: number }): JSX.Element => (
  <Heading size="300" mb={['4x', '1x']}>
    {props.step}. {props.children}
  </Heading>
);

HowBrandWorks.ItemContent = Text;

export default HowBrandWorks;
